import { RefabricProURL } from "../index";
import { usageCsvRequestConfig } from "./usageCsv";
import { ApiResponse } from "../HttpClient";
import {
  ITeamsSendInviteRequest,
  ITeamsAddMemberRequest,
  ITeamsEditMemberRoleRequest,
  ITeamsInviteRequest,
  ITeamsInviteResponse,
  ITeamsAcceptMemberInviteResponse,
  ITeamsRemoveMemberResponse,
  ITeamsCancelInviteResponse,
  ITeamRosterResponse,
  ITeamCreditUsageResponse,
  IMemberCreditShareRequest,
  IMemberCreditShareResponse,
  IMemberPermissionsResponse,
  IMemberPermissionsRequest,
  ITeamCreditPolicyRequest,
  ITeamCreditPolicyResponse,
  TeamUsageWindow,
  ITeamUsageReportResponse,
  ITeamUsageItemsRequest,
  ITeamUsageItemsResponse,
  ITeamUsageItemsCsv,
} from "./teams.type";

export default {
  async sendInvite(data: ITeamsSendInviteRequest): Promise<ApiResponse<any>> {
    return await RefabricProURL.post("teams/send_invite", data, {});
  },
  // P4-F4: the token in the BODY; no e-mail — the backend takes the address from the invite row.
  async acceptInvite(link_token: string): Promise<ApiResponse<any>> {
    return await RefabricProURL.post("teams/accept_invite", { link_token }, {});
  },
  async addMember(data: ITeamsAddMemberRequest): Promise<ApiResponse<any>> {
    return await RefabricProURL.post("teams/member", data, {});
  },
  async editMemberRole(member_id: string, data: ITeamsEditMemberRoleRequest): Promise<ApiResponse<any>> {
    return await RefabricProURL.put(`teams/${member_id}/role`, data, {});
  },
  async deleteMember(member_id: string): Promise<ApiResponse<any>> {
    return await RefabricProURL.delete(`teams/${member_id}`, {});
  },
  async getTeamMembers(): Promise<ApiResponse<any>> {
    return await RefabricProURL.get(`teams/members`, {});
  },
  async editTeamMembersRoles(items: Array<{ uid: number; role: string }>): Promise<ApiResponse<any>> {
    return await RefabricProURL.put(`teams/roles`, { items }, {});
  },
  async deleteTeamMembers(ids: Array<{ uid: string }>): Promise<ApiResponse<any>> {
    return await RefabricProURL.delete(`teams/delete`, { data: { items: ids } });
  },
  async getTeamInfo(): Promise<ApiResponse<any>> {
    return await RefabricProURL.get(`teams/team_info`, {});
  },

  // New endpoints
  async invite(data: ITeamsInviteRequest): Promise<ApiResponse<ITeamsInviteResponse>> {
    return await RefabricProURL.post("teams/invite", data, {});
  },
  async acceptMemberInvite(link_token: string): Promise<ApiResponse<ITeamsAcceptMemberInviteResponse>> {
    return await RefabricProURL.post("teams/accept_member_invite", { link_token }, {});
  },
  async removeMember(user_id: number): Promise<ApiResponse<ITeamsRemoveMemberResponse>> {
    return await RefabricProURL.delete(`teams/members/${user_id}`, {});
  },
  async getRoster(): Promise<ApiResponse<ITeamRosterResponse>> {
    return await RefabricProURL.get(`teams/roster`);
  },
  async cancelInvite(invite_id: number): Promise<ApiResponse<ITeamsCancelInviteResponse>> {
    return await RefabricProURL.delete(`teams/invites/${invite_id}`, {});
  },

  // REF-5549 (BE REF-5446): per-member credit allocation + permissions (admin-only).
  // 403/409 reject for inline UX — credit-usage is exempted in HttpClient; members/* already are.
  async getCreditUsage(): Promise<ApiResponse<ITeamCreditUsageResponse>> {
    return await RefabricProURL.get(`teams/credit-usage`);
  },
  async setMemberCreditShare(user_id: number, data: IMemberCreditShareRequest): Promise<ApiResponse<IMemberCreditShareResponse>> {
    return await RefabricProURL.put(`teams/members/${user_id}/credit-share`, data, {});
  },
  // REF-5611: team-wide "all members share the pool" switch. Super-admin only — 403
  // super_admin_required rejects for inline UX (teams/credit-policy is exempted in HttpClient).
  async setTeamCreditPolicy(data: ITeamCreditPolicyRequest): Promise<ApiResponse<ITeamCreditPolicyResponse>> {
    return await RefabricProURL.put(`teams/credit-policy`, data, {});
  },
  async getMemberPermissions(user_id: number): Promise<ApiResponse<IMemberPermissionsResponse>> {
    return await RefabricProURL.get(`teams/members/${user_id}/permissions`);
  },
  async setMemberPermissions(user_id: number, data: IMemberPermissionsRequest): Promise<ApiResponse<{ response_message: string; user_id: number }>> {
    return await RefabricProURL.put(`teams/members/${user_id}/permissions`, data, {});
  },

  // BE REF-5515: admin-only team usage report (KPIs + per-tool + per-member + daily trend).
  // Errors reject with a stable error_code (teams.admin_required 403 / teams.team_not_found 404 /
  // teams.invalid_window 400) for inline UX — exempted in HttpClient.
  async getUsageReport(window: TeamUsageWindow): Promise<ApiResponse<ITeamUsageReportResponse>> {
    return await RefabricProURL.get(`teams/usage-report`, { params: { window } });
  },
  // REF-6975: per-transaction spend detail (same guard + error_codes as getUsageReport; the
  // `teams/usage-report` HttpClient exemption covers this path too, so 403/404 reject).
  async getUsageReportItems(params: ITeamUsageItemsRequest): Promise<ApiResponse<ITeamUsageItemsResponse>> {
    return await RefabricProURL.get(`teams/usage-report/items`, { params });
  },
  // REF-6975: the same filtered list as CSV (no paging; BE caps it — 1,000 rows for trial viewers, 10,000 otherwise — and sets the
  // CORS-exposed `X-Truncated: true` when more matched). HttpClient.get returns only the body, so the
  // header is folded into it inside axios's transform step (usageCsvRequestConfig). Resolves to { file, truncated } on success;
  // a 401/503 still resolves to the interceptor's `_handled` envelope [HTTP_HANDLED_RESPONSE] —
  // callers must check for `file`, never assume one came back.
  async getUsageReportItemsCsv(params: Omit<ITeamUsageItemsRequest, "page" | "page_size">): Promise<ITeamUsageItemsCsv | ApiResponse<null>> {
    return (await RefabricProURL.get(`teams/usage-report/items.csv`, usageCsvRequestConfig(params))) as unknown as ITeamUsageItemsCsv | ApiResponse<null>;
  },
};
